import { CommonModule } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import type { OnInit } from '@angular/core';
import {
	ChangeDetectorRef,
	Component,
	inject,
	input,
	ViewChild,
} from '@angular/core';
import type { NgForm } from '@angular/forms';
import { FormGroup } from '@angular/forms';

import type { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { RecaptchaNotificationComponent } from '@valk-nx/components/ui-recaptcha-notification/src/lib/recaptcha-notification';
import type { Language } from '@valk-nx/core/lib/core';
import { HOTEL_SLUG, RECAPTCHA_V2_SITE_KEY } from '@valk-nx/core/lib/core';
import type { CheckedOption } from '@valk-nx/core/lib/interfaces/form.interface';
import { SharedFormlyModule } from '@valk-nx/formly/lib/formly.module';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import type {
	HotelRecipients,
	Recipients,
} from '@valk-nx/services/form/src/lib/form.service';
import { FormService } from '@valk-nx/services/form/src/lib/form.service';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import type {
	FormInputInterface,
	FormSelectInterface,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

@Component({
	selector: 'sb-form',
	templateUrl: './form.html',
	imports: [
		AlertComponent,
		CommonModule,
		TranslatePipe,
		RecaptchaNotificationComponent,
		StoryblokDirective,
		SharedFormlyModule,
	],
	host: { class: 'w-full' },
})
export class FormComponent extends StoryblokRootDirective implements OnInit {
	private readonly cd = inject(ChangeDetectorRef);
	private readonly formService = inject(FormService);
	private readonly translate = inject(TranslateService);
	readonly hotelSlug = inject(HOTEL_SLUG);
	readonly recaptchaV2Key = inject(RECAPTCHA_V2_SITE_KEY);

	subject = input.required<string>();
	recipient = input<Recipients>();
	hotelRecipients = input<HotelRecipients | undefined>();
	blocks = input.required<(FormInputInterface & FormSelectInterface)[]>();
	buttonText = input.required<string>();
	successTitle = input.required<string>();
	successContent = input.required<string>();

	@ViewChild('formRef') formRef!: NgForm;
	formFields: FormlyFieldConfig[] = [];
	formGroup = new FormGroup({});
	options: FormlyFormOptions = {};
	model = {};

	blocksWithId: (FormInputInterface &
		FormSelectInterface & { id: string })[] = [];
	parsedFormData: {
		id: string;
		label: string;
		value: string | CheckedOption[];
	}[] = [];
	requestSubmitted = false;
	requestSubmitting = false;
	language: Language;
	recaptchaVersion: 'v2' | 'v3' = 'v3';

	constructor() {
		super();
		this.language = (this.translate.getCurrentLang() ??
			this.translate.getFallbackLang()) as Language;
	}

	ngOnInit() {
		this.blocksWithId = this.blocks().map((block) => ({
			...block,
			id: block._uid,
		}));
		this.formFields = StoryblokHelper.buildFormlyData(this.blocksWithId);
	}

	async onSubmit(): Promise<void> {
		if (this.formGroup.valid) {
			this.requestSubmitting = true;

			this.parsedFormData = StoryblokHelper.parsedFormData(
				this.formGroup.value,
				this.blocksWithId,
			).filter((formField) => !!formField); // filter out null value that is set on storyblok.helper.ts line 408

			const emailRecipients: string[] =
				StoryblokHelper.parseEmailRecipientFormData(
					this.blocksWithId,
					this.parsedFormData,
				);

			return this.formService
				.submitCustomForm(
					this.subject(),
					this.parsedFormData,
					this.recipient(),
					this.hotelRecipients(),
					this.hotelSlug,
					emailRecipients,
					this.formGroup.get('recaptcha')?.value,
				)
				.then((response) => {
					this.requestSubmitting = false;
					this.requestSubmitted = response.success;
					this.useRecaptchaV2(response.status);
					this.cd.detectChanges();
				})
				.catch((err: HttpErrorResponse) => {
					this.requestSubmitting = false;
					this.useRecaptchaV2(err.status);
					this.cd.detectChanges();
				});
		} else {
			this.scrollToError();
		}
	}

	resetForm(): void {
		this.requestSubmitted = false;
		this.requestSubmitting = false;

		this.formGroup.reset();
		this.formRef.resetForm();
	}

	scrollToError() {
		setTimeout(() => {
			GeneralHelper.scrollToElementWithClass(
				'form-field-error',
				undefined,
				'input',
			);
		}, 100);
	}

	useRecaptchaV2(status: number | undefined) {
		if (
			status === 403 &&
			this.recaptchaVersion === 'v3' &&
			this.recaptchaV2Key
		) {
			this.recaptchaVersion = 'v2';
			this.formFields = [
				...this.formFields,
				{
					id: 'recaptcha',
					key: 'recaptcha',
					props: {
						errorMode: 'handled',
						required: true,
						siteKey: this.recaptchaV2Key,
						size: 'default',
					},
					type: 'recaptchaV2',
				},
			];
		}
	}
}
